export function isArrayEmpty<T>(array: T[]): boolean {
  return !array || array.length === 0;
}

export function isArrayNotEmpty<T>(array: T[]): boolean {
  return !!array && array.length > 0;
}

export function firstArrayElement<T>(array: T[]): T | undefined {
  return array?.length > 0 ? array[0] : undefined;
}

export function lastArrayElement<T>(array: T[]): T | undefined {
  return array?.length > 0 ? array[array.length - 1] : undefined;
}

export function arrayToKeyValuePairs<T, K extends keyof T, V extends keyof T>(
  array: T[],
  keyField: K,
  valueField: V,
): Record<string, T[V]> {
  if (!array) {
    return;
  }
  return Object.fromEntries(
    array?.map((item) => [String(item[keyField]), item[valueField]]),
  );
}

export function filterDuplicatesInArray<T, K extends keyof T>(
  array: T[],
  keyField: K,
): T[] {
  if (!isArrayNotEmpty(array)) {
    return [];
  }

  const seenKeys = new Set<any>();
  return array?.filter((item) => {
    const keyValue = item[keyField];
    if (seenKeys.has(keyValue)) {
      return false;
    }
    seenKeys.add(keyValue);
    return true;
  });
}

export function addItemToArray<T>(array: T[], item: T) {
  if (isArrayEmpty(array)) {
    if (!item) return [];
    return [item];
  }

  if (!item) return array;

  return [...array, item];
}

export function sortArrayAsc<T, K extends keyof T>(array: T[], field: K): T[] {
  if (!array || array.length === 0) return [];
  return [...array].sort((a, b) => {
    const aValue = a[field];
    const bValue = b[field];
    return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
  });
}

export function sortArrayDesc<T, K extends keyof T>(array: T[], field: K): T[] {
  if (!array || array.length === 0) return [];
  return [...array].sort((a, b) => {
    const aValue = a[field];
    const bValue = b[field];
    return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
  });
}

export function modifyArrayItemById<T extends { id: number }>(
  array: T[],
  partialItem: Partial<T> & { id: number },
): T[] {
  return array.map((element) =>
    element.id === partialItem.id ? { ...element, ...partialItem } : element,
  );
}

export function insertAt<T>(arr: T[], index: number, item: T): T[] {
  const copy = [...arr];
  copy.splice(index, 0, item);
  return copy;
}

export function compareBy<T, K>(mapper: (item: T) => K) {
  return (a: T, b: T) => {
    const aValue = mapper(a);
    const bValue = mapper(b);
    return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
  };
}

// Ascending, locale-aware string comparator — unlike compareBy (which sorts
// descending and compares raw values with </>), this is for text that needs
// proper locale ordering (accents, casing) rather than UTF-16 code unit
// comparison.
export function compareByLocale<T>(mapper: (item: T) => string) {
  return (a: T, b: T) => mapper(a).localeCompare(mapper(b));
}

export function arrayMaxBy<T, K>(array: T[], mapper: (item: T) => K): T | null {
  if (isArrayEmpty(array)) return null;
  return array.reduce((max, item) => {
    if (mapper(item) >= mapper(max)) return item;
    return max;
  }, array[0]);
}
